import {useMaterialQualityKpis} from '@domain/kpis'
import {DataContentWrapper} from '@hconnect/common/containers'
import {Iso8601, TimeZone} from '@hconnect/common/types'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, SxProps, Theme, Typography, useMediaQuery, useTheme} from '@mui/material'
import moment from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {QualityParametersTable} from '../components'
import {KPIS_WIDGET_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  from: Iso8601 | undefined
  to: Iso8601 | undefined
  timezone: TimeZone
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
  isVerticalLayout: boolean
  timeRangeLabel?: string
}

const cardBoxPx = {xs: 2, md: 3}

export const MaterialQualityParametersContainer: React.FC<Props> = ({
  plantId,
  cardBoxSx,
  from,
  to,
  timeRangeLabel,
  placeholderWrapperSx,
  timezone,
  isVerticalLayout
}) => {
  const {t} = useTranslation(KPIS_WIDGET_NAMESPACE)
  const theme = useTheme()
  const isCompactView = useMediaQuery(theme.breakpoints.down('md'))
  const threeMonthsAgo = moment.tz(timezone).subtract(3, 'months').startOf('day')
  const isTimeRangeSupported = from ? moment.tz(from, timezone).isAfter(threeMonthsAgo) : false
  const isShiftTimeRangeSupported = !!from && !!to

  const {data, isLoading, isError, isRefetching, refetch} = useMaterialQualityKpis(
    {
      plantId,
      from,
      to
    },
    {
      enabled: isTimeRangeSupported && isShiftTimeRangeSupported
    }
  )

  return (
    <CardBox
      sx={{
        ...cardBoxSx,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        px: 0,
        pl: cardBoxPx
      }}
      data-test-id="quality-parameters-card"
    >
      <Box
        display="flex"
        flexDirection="row"
        sx={{
          display: 'flex',
          pr: cardBoxPx
        }}
      >
        <Box flex={1}>
          <Typography variant="h3" mb={2}>
            {t('qualityParameters.cardTitle')}
          </Typography>
        </Box>
        {timeRangeLabel && (
          <Chip
            icon={<CalendarTodayOutlined />}
            label={timeRangeLabel}
            size="small"
            sx={{mx: 1}}
            data-test-id="quality-parameters-card-chip"
          />
        )}
      </Box>
      <DataContentWrapper
        showLoader={isLoading}
        data={data}
        isError={isError}
        isRefetching={isRefetching}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
        noDataSettings={{
          ...(!(isTimeRangeSupported && isShiftTimeRangeSupported) && {
            noDataMessage: t(
              !isShiftTimeRangeSupported
                ? 'qualityParameters.message.unsupportedShiftTimeRange'
                : 'qualityParameters.message.unsupportedTimeRange'
            )
          })
        }}
      >
        <Box sx={{overflowY: 'auto', height: isVerticalLayout ? '100%' : 'auto', pr: cardBoxPx}}>
          <QualityParametersTable
            data={data}
            isVerticalLayout={isVerticalLayout}
            isCompactView={isCompactView}
          />
        </Box>
      </DataContentWrapper>
    </CardBox>
  )
}
